/**
 * Capability gates keyed off the connected viewer's contract version. The UI is
 * built ahead of the engine waves (v13–v16); a control whose RPC hasn't shipped
 * yet renders disabled with a "coming soon" hint instead of erroring. As each
 * engine wave lands and the contract bumps, its features light up automatically.
 */
export interface Caps {
  version: number;
  /** v49 — setBackgroundImageFraming / getBackgroundImageFraming. */
  backgroundFramingV49: boolean;
  /** v50 — Catalogs.stoneCuts / .prongVariants (the two-axis gem picker). */
  ringGemMatrixV50: boolean;
  /** v54 — `setChannelManual`, a brand-new method name: on an older viewer the
   *  call never returns (RPC has no timeout), so the Cut rows must not offer it. */
  ringChannelManualV54: boolean;
  /** v55 — `setCrossChannel` / `setCrossChannelManual`, both brand-new method
   *  names: on an older viewer the call never returns, so the cross-channel Cut
   *  rows must not offer them. */
  ringCrossChannelV55: boolean;
  /** v56: `HeadUnderstructure` gains "cathedral-basket" and `ProngTip` gains "v".
   *  New VALUES on existing setters, so an older viewer cannot hang on them —
   *  but it also cannot build them, so the two rows are gated rather than the
   *  methods. Everything else in W22 (the collet, the girdle-polygon seat, the
   *  heart convention, the Band group, the bridge-accent row) rides methods that
   *  already existed and needs no flag. */
  ringHeadV56: boolean;
  /** v57: the channel's cut DEPTH is ownable, so the style can derive it from
   *  the stone (0.30 d) instead of a flat 0.6 mm. `setChannelManual` is v54, so
   *  nothing can hang — an older viewer stores the field and simply does not
   *  derive it. This gates only the derived/reset affordance. */
  ringChannelDepthV57: boolean;
  /** v58 — the node inspector: `getNodeDetails` / `setNodeTransform` /
   *  `setNodeFlags` / `renameNode` / `getNodeGeometryInfo`. FIVE brand-new
   *  method NAMES, so this flag is not optional: an older viewer answers
   *  `Unknown method` and RPC has no timeout, which hangs the promise forever
   *  with nothing on screen to say why. */
  ringNodeInspectorV58: boolean;
  /** v60 — the split viewport: `setViewportLayout` / `getViewportLayout` /
   *  `setViewportPaneMode` / `setActiveViewportPane`. FOUR brand-new method
   *  NAMES, so this flag is not optional: an older viewer answers
   *  `Unknown method` and RPC has no timeout, so the toggle would hang the
   *  promise forever and the editor would sit there with nothing to explain it. */
  viewportQuadV60: boolean;
  /** v61 — a locked pane ORBITS about one world axis; `ViewportPaneMode` becomes
   *  `x`/`y`/`z`/`perspective`. No new method NAME (`setViewportPaneMode` is
   *  v60), so RPC cannot hang — but a v60 viewer cannot BUILD the new values, so
   *  the switches must not offer them. Same shape as `ringHeadV56`. */
  viewportAxisLockV61: boolean;
  /** v63 — On Body: `placeOnBody` / `clearOnBody` / `setBodyOccluder` /
   *  `getBodyOccluder` / `getModelFit`. FIVE brand-new method NAMES, so this
   *  flag is not optional: an older viewer answers `Unknown method` and RPC has
   *  no timeout, so the call would hang forever. (`MODEL_ROOT_NODE_ID` rides
   *  the v58 node inspector and needs this flag too — a v58–v62 viewer cannot
   *  resolve it and answers null.) */
  onBodyV63: boolean;
  /** v64 — On Body's anchor gains `along` / `axisWorld` / `dorsal`, the params
   *  `headToCamera`, the occluder `contactShadow`. Fields only — no new method
   *  name, so nothing can hang; the flag only hides the two rows (stone faces
   *  camera, contact shadow) a v63 viewer would ignore. */
  onBodyV64: boolean;
  /** v65 — the whole scene in one file: `exportSceneFile` / `exportSceneJSON` /
   *  `importScene` / `inspectSceneFile`. FOUR brand-new method NAMES, so this
   *  flag is not optional: an older viewer answers `Unknown method` and RPC has
   *  no timeout, so Save / Open scene file would hang forever. It also gates the
   *  fields that ride existing methods — `LoadOptions.embeddedScene`,
   *  `GlbExportOptions.embedScene` / `sceneHost`, `ModelInfo.embeddedScene` and
   *  the `sceneApplied` event — so an older viewer is sent exactly what it was
   *  sent before. */
  sceneFileV65: boolean;
  /** v66 — CNC program export: `exportBandCnc` / `exportBandCncBatch` /
   *  `previewBandCnc` / `getCncDefaultProfile` on RingBuilderApi. FOUR
   *  brand-new method NAMES, so this flag is not optional: an older viewer
   *  answers `Unknown method` and RPC has no timeout, so the CNC dialog would
   *  hang forever. Every CNC path in the host (the Manufacturing entry, the
   *  dialog, the preview, the batch sizes and the .3dm download) is gated on
   *  it — v66 never shipped without all four, so one flag covers them. */
  ringCncV66: boolean;
  /** v66 — path grooves: `GrooveDirection` "path", `DesignGroove.path` /
   *  `.preset` and the bypass-shoulder preset. They ride `setDesignGrooves` /
   *  `updateDesignGroove`, existing names, so RPC cannot hang on them — but
   *  this flag is not optional either: an older viewer's `clampDesignGrooves`
   *  reads any direction that is not "vertical" as "horizontal" and keeps
   *  `positionMM` 0, so a path groove sent to it would be carved as a full
   *  groove round the centre line. The Joints tab offers path grooves only
   *  with this. */
  ringPathGroovesV66: boolean;
  /** v67 — `applyRingDesign` / `loadRingState` on RingBuilderApi: a whole
   *  design (or a saved RingState) in ONE viewer-side rebuild. TWO brand-new
   *  method NAMES, so this flag is not optional: an older viewer answers
   *  `Unknown method` and RPC has no timeout. Without it the host keeps the
   *  field-by-field setter replay (`ringState.applyRingState`). Also gates the
   *  v67 fields that ride existing setters — `DiamondSetting` "pave" /
   *  "shared-prong", `DiamondConfig.junctionScale` and the 0.15 mm groove
   *  floor — which an older viewer would ignore or clamp. */
  ringDesignV67: boolean;
  /** v67 (band designs W3) — the parametric "param" cross-section, the
   *  partial step and the true-V groove angle. They ride `applyRingDesign` /
   *  `setDesignGrooves`, so this is `ringDesignV67` by another name: a
   *  pre-v67 viewer would not list "param", would carve a partial step's path
   *  groove as a plain groove and ignore `vAngleDeg`. */
  ringBodyV67: boolean;
  /** v67 (W4) — `EngravingConfig.logo`. Rides `setEngraving`; an older viewer
   *  would store it and never draw it, so the Logo rows stay hidden. */
  ringLogoV67: boolean;
  /** v67 (W4) — "Start from a design". A recipe is restored with
   *  `loadRingState` (a v67 method NAME) plus `removeRing` / `addRing`, and
   *  its rings use the v67 fields above. */
  ringRecipesV67: boolean;
  /** v68 — the photographed pose: `setBandLayout(preset, pose)` (an existing
   *  name gaining an optional argument) and `getBandLayout()`, a brand-new
   *  method NAME — so this flag is not optional: an older viewer answers
   *  `Unknown method` and RPC has no timeout. Without it a recipe's pose is
   *  left out (the preset's shared law) with a note. */
  ringPoseV68: boolean;
  /** v68 — a recipe's named look ("catalogue-white"). Only existing scene RPCs,
   *  but its light-tent environment asset ships only with a v68 viewer, so this
   *  is `ringPoseV68` under the name of what it gates. */
  ringLookV68: boolean;
  /** v68 — `DiamondConfig.junctionOptions` (bead sides / ends / growth) and
   *  `StonePattern.seatRimMM` (the rim kept beside each seat). Both ride
   *  `applyRingDesign` / `loadRingState`; an older viewer ignores them, so this
   *  is `ringPoseV68` by another name, for the rows it gates. */
  ringJunctionV68: boolean;
  /** v51 — `setMilgrain`, a NEW METHOD NAME. */
  ringMilgrainV51: boolean;
  /** v68 (HDR editor) — `pickSurface`, a NEW METHOD NAME: HDR Studio's
   *  "Place highlight" raycasts the model through it. RPC has no timeout, so
   *  without this the button is disabled ("Needs viewer v68"). */
  hdrPickV68: boolean;
  /** v68 (HDR editor) — not a method name but a FIX the host relies on: with
   *  `forceSceneEnvironment` on, a scene-env load re-points the stones at the
   *  new equirect (`Viewer.onSceneEnvChanged`). Before it, every load leaked a
   *  GL texture and the stones kept the previous map, so HDR Studio sends the
   *  stones their own copy of each push on older viewers. */
  hdrStonesShareSceneV68: boolean;
  /** v68 (HDR editor) — `loadEnvFromPixels`, a NEW METHOD NAME: HDR Studio's
   *  live map pushes go as raw half-float pixels (no file, no decode). RPC has
   *  no timeout, so older viewers get the map as an EXR File instead. */
  hdrEnvPixelsV68: boolean;
  /** v52 — `applyFreePattern`'s count/spanDeg options + `FreeStone.sizeScale`. */
  ringFreePatternV52: boolean;
  // v13
  rendererParams: boolean;
  backgroundImage: boolean;
  transparentBg: boolean;
  diamondEnv: boolean;
  postFxComposite: boolean; // dof / CA / grain / lut
  taa: boolean;
  sscsSsgiBevel: boolean;
  snapshotFormat: boolean;
  // v14
  outline: boolean;
  picker: boolean;
  sceneTree: boolean;
  transformGizmo: boolean;
  toolbarOps: boolean; // mirror / reset rotation / delete-node / frame-node
  physicalMaterial: boolean;
  materialPresets: boolean;
  autoAssign: boolean;
  khrVariants: boolean;
  // v15
  gltfAnim: boolean;
  cameraViews: boolean;
  loaders: boolean;
  tiledExport: boolean;
  videoMp4: boolean;
  vjsonWriter: boolean;
  // v16
  geometryGen: boolean;
  computePose: boolean;
  hdriGround: boolean;
  stageProps: boolean;
  textDecal: boolean;
  simplify: boolean;
  objectRotation: boolean;
  // v24
  exportFull: boolean;     // full GLB / video / image option set
  utilityPlugins: boolean; // loading screen / full screen / frame fade / S3 / extras
  // v25
  nichePlugins: boolean;   // physics / snow / wave / watch hands / css3d / scroll / xatlas / rainbow
  // v26
  finalParity: boolean;    // advanced env slot / widgets / diamond env 2-3 / zoom
  // v31: iJewel field-gap closeouts. Mainly guards brand-new RPC methods
  // (clearMaterialVariations / getGroupMaterialInfo / renameSwitchNode /
  // setSwitchNodeConfig / setHoveredGroup / addStageModelFile /
  // setStageGroundBelow / setGroupGemEnvSlot / activateDiamondEnvSlot) —
  // calling a method name a pre-v31 viewer doesn't implement leaves the RPC
  // promise hanging forever (ViewerClient has no call timeout), so those
  // controls must stay hidden until this is true. Also used to pick the
  // backward-compatible wire value/branch for fields whose semantics changed
  // (RendererParams.mode 'custom' vs legacy 'preview', snapshot crop/frames).
  v31: boolean;
  // v32: wave-2 feature surface — gem inclusions (setGroupGemInclusions),
  // randomized self-shadow (get/setRandomizedSelfShadow), the Variation
  // Configurator, Virtual Cameras, Scene Presets, the Scene Loop kiosk
  // playlist, and the ScrollableCameraView live driver (setScrollProgress /
  // generateScrollViewsCode) are all BRAND-NEW method names — same
  // hang-forever risk as v31, so gate those controls behind this. Fields that
  // merely extend an existing patch-style setter (LayeredMaterial via
  // get/setGroupMaterialExtensions, drag/scroll GLTF options via
  // setGltfAnimationOptions, MotionSettings via setPostFX) don't need it —
  // legacy viewers ignore unknown patch fields.
  v32: boolean;
  // v33: the engagement-ring configurator mode ("/engagement" route, contract
  // EngagementApi). New method/event names — same hang-forever risk as v31/v32.
  engagement: boolean;
  // v34: engagement `ShankConfig` gains the band-grade surface fields
  // (edgeStyle/grooves/channel/splitMode/splitPatternId/materials/diamond).
  // No new RPC methods (same `setShank` patch setter), but a pre-v34 viewer's
  // `EngagementState.shank` simply lacks these fields — reading e.g.
  // `shank.grooves.pattern` would throw, not hang, so gate rendering on this.
  engagementBandSurface: boolean;
  // v35: engagement spec/validator layer + manufacturing export. New RPC
  // method `exportManufacturingGLB` — same hang-forever risk as v31/v32/v33
  // on a pre-v35 viewer, so the Download GLB/STL buttons gate on this.
  // `spec.warnings`/`spec.metalWeightGrams` are now populated (present but
  // empty on earlier engagement viewers, never throwing), so reads don't
  // need this gate — only the export RPC does.
  engagementSpecV35: boolean;
  // v36: "complete the set" — engagement mode can stack a matching wedding band
  // (addCompanionBand / removeCompanionBand / setCompanionBandLayout). New RPC
  // method names — same hang-forever risk on a pre-v36 viewer, so the companion-
  // band toggle + offset slider gate on this.
  companionBandV36: boolean;
  // v37: the merged Ring Builder facade (band + engagement in one route).
  // Guards the brand-new method names — getRingState / setEngagementEnabled /
  // setEngagement plus the new geometry setters setRingShape / setDivision /
  // setComfortFit / setJointGrooves — and the `ringStateChanged` event. On a
  // pre-v37 viewer these REJECT with "Unknown method" (RpcHost) rather than
  // hanging, but the UI must still not offer a control the viewer can't honour,
  // so fall back to the band/engagement facades when this is false.
  ringBuilderV37: boolean;
  // v38: `setQualityTier` (the Ring Builder's Medium/High/Ultra gear menu). A
  // brand-new method name — a pre-v38 viewer REJECTS it ("Unknown method"), so
  // hide the control rather than offer a switch that throws.
  qualityTierV38: boolean;
  // v38 geometry: setEdgeSides / setInsidePolished / setAutoThickness /
  // setRingSizeUK are brand-new method names (a pre-v38 viewer REJECTS them),
  // and `grooves.seamBoundaries` would be stored but never honoured. The
  // Octagon/Wavy silhouettes and the two milgrain joint types need no gate —
  // they ride the existing setRingShape / setJointGrooves and simply do not
  // appear in an older viewer's catalogs.
  ringGeometryV38: boolean;
  // v39 diamonds: side placement, the full setting/spacing/colour vocabulary,
  // gold beads and the inner stone. Almost the whole wave rides the EXISTING
  // `setDiamond(Partial<DiamondConfig>)` patch, which a pre-v39 viewer accepts
  // and then ignores the unknown fields of — so the gate is about not offering
  // a control that would silently do nothing, plus the one brand-new method
  // name (`setInnerDiamond`, which a pre-v39 viewer REJECTS). The new
  // Vertical/Clamp/Clamp∠ styles and the Colour/Spacing/Setting pickers need no
  // separate gate: they come from `getCatalogs()` and are simply absent on an
  // older viewer.
  ringDiamondsV39: boolean;
  // v40 free placement: setFreeStones / addFreeStone / updateFreeStone /
  // removeFreeStone / applyFreePattern / setFreeRepeat / setFreeRotate are all
  // brand-new method names, which a pre-v40 viewer REJECTS ("Unknown method").
  // The `free-place` style tile and the `freePatterns` picker come from
  // `getCatalogs()` and are simply absent on an older viewer, so this gate is
  // about the editor itself, not the tile.
  ringFreeStonesV40: boolean;
  // v41 long tail: addDesignGroove / updateDesignGroove / removeDesignGroove /
  // setDesignGrooves / setCustomRatios / setPartitionLabel / addRing /
  // removeRing / getRingCount are brand-new method names, which a pre-v41
  // viewer REJECTS ("Unknown method"). `addSecondRing`/`removeSecondRing` still
  // work everywhere, so a pair is available with or without this gate.
  ringLongTailV41: boolean;
  // v42 wave F: setPartitionCount / setRingsLinked / getRingsLinked /
  // flipCamera are brand-new method names, which a pre-v42 viewer REJECTS
  // ("Unknown method"). The Quad/Multi split-mode tiles need no separate gate
  // beyond this one: their patterns come from `getCatalogs()` and are simply
  // absent on an older viewer, but `setSplitMode("quad")` on a pre-v42 viewer
  // would store a mode with no pattern behind it, so gate the tiles too.
  // `zoomCamera` is v26 and needs no gate of its own.
  ringWaveFV42: boolean;
  // v43 ring manager: setRingVisible / moveRing are brand-new method names,
  // which a pre-v43 viewer REJECTS ("Unknown method"), so the modal's Hide and
  // Move ←/→ controls gate on this. Nothing else in the wave needs a gate: the
  // groove Surface row, the engraving relief block and the diamond
  // specification fields all ride existing patch setters (setGrooves /
  // setEngraving / setDiamond / updateFreeStone), which an older viewer accepts
  // and then ignores the unknown fields of; the four new pattern tiles and the
  // three specification lists come from `getCatalogs()` and are simply absent.
  // `removeRing(0)` is not gated either — a pre-v43 viewer no-ops it rather
  // than failing, so the row's Delete stays available, just inert on ring 0.
  ringManagerV43: boolean;
  // v45 persistence + the metal-env escape hatch. Two brand-new method names —
  // `clearMetalEnv()` (drop a Metal HDR override so the Environment picker
  // reaches the metals again; before this the first Metal HDR load was
  // permanent) and `getGroupGemInclusions(id)` (read a gem group's real
  // inclusion state instead of the panel guessing its own defaults) — which a
  // pre-v45 viewer REJECTS ("Unknown method"), so both call sites gate on this.
  // The three new SceneState fields (backgroundImageUrl / metalEnvUrl /
  // gemInclusions) need no gate: they ride getSceneState/_applySceneState and
  // are simply absent on an older viewer.
  v45: boolean;
  /**
   * v48 — `restoreDefaultMetalEnv()`, a BRAND-NEW METHOD NAME, so it needs a
   * gate: RPC has no timeout and a v47 viewer would leave the promise hanging
   * forever. The two SceneState fields that came with it (`metalEnvActive`,
   * `metalEnvSource`) and `SceneState.configurator` need none — they ride
   * getSceneState/_applySceneState and are simply absent on an older viewer.
   */
  v48: boolean;
}

export function capsFor(version: number): Caps {
  const v = (n: number) => version >= n;
  return {
    version,
    v48: v(48),
    rendererParams: v(13),
    backgroundImage: v(13),
    transparentBg: v(13),
    diamondEnv: v(13),
    postFxComposite: v(13),
    taa: v(13),
    sscsSsgiBevel: v(13),
    snapshotFormat: v(13),
    outline: v(14),
    picker: v(14),
    sceneTree: v(14),
    transformGizmo: v(14),
    toolbarOps: v(14),
    physicalMaterial: v(14),
    materialPresets: v(14),
    autoAssign: v(14),
    khrVariants: v(14),
    gltfAnim: v(15),
    cameraViews: v(15),
    loaders: v(15),
    tiledExport: v(15),
    videoMp4: v(15),
    vjsonWriter: v(15),
    geometryGen: v(16),
    computePose: v(16),
    hdriGround: v(16),
    stageProps: v(16),
    textDecal: v(16),
    simplify: v(16),
    objectRotation: v(16),
    exportFull: v(24),
    utilityPlugins: v(24),
    nichePlugins: v(25),
    finalParity: v(26),
    v31: v(31),
    v32: v(32),
    engagement: v(33),
    engagementBandSurface: v(34),
    engagementSpecV35: v(35),
    companionBandV36: v(36),
    ringBuilderV37: v(37),
    qualityTierV38: v(38),
    ringGeometryV38: v(38),
    ringDiamondsV39: v(39),
    ringFreeStonesV40: v(40),
    ringLongTailV41: v(41),
    ringWaveFV42: v(42),
    ringManagerV43: v(43),
    v45: v(45),
    // v49: setBackgroundImageFraming / getBackgroundImageFraming are NEW method
    // names, and RPC has no timeout — calling them on an older viewer hangs the
    // promise forever, so the backdrop framing rows stay hidden without this.
    backgroundFramingV49: v(49),
    // v50: `Catalogs.stoneCuts` / `.prongVariants` — the stone catalogue as two
    // axes. No new METHOD name, so nothing can hang; the flag exists because a
    // v49 viewer simply omits the fields and the Gems tab must fall back to the
    // flat 48-tile list rather than render two empty rows.
    ringGemMatrixV50: v(50),
    ringChannelManualV54: v(54),
    ringCrossChannelV55: v(55),
    ringHeadV56: v(56),
    ringChannelDepthV57: v(57),
    ringNodeInspectorV58: v(58),
    viewportQuadV60: v(60),
    viewportAxisLockV61: v(61),
    onBodyV63: v(63),
    onBodyV64: v(64),
    sceneFileV65: v(65),
    // v66: exportBandCnc / exportBandCncBatch / previewBandCnc /
    // getCncDefaultProfile are NEW method names — without this the CNC dialog
    // would call into a viewer that never answers.
    ringCncV66: v(66),
    // v66: path grooves ride existing setters, so nothing can hang — but an
    // older viewer would carve a "path" groove as a centre-line groove all the
    // way round, so the controls stay hidden without this.
    ringPathGroovesV66: v(66),
    // v67: applyRingDesign / loadRingState are NEW method names (RPC has no
    // timeout); the pavé settings, junctionScale and 0.15 mm grooves ride it.
    ringDesignV67: v(67),
    // v67 band designs W3/W4: no new method name (everything rides
    // applyRingDesign / loadRingState / setEngraving / setDesignGrooves), so
    // these are ringDesignV67 under the name of the rows they gate.
    ringBodyV67: v(67),
    ringLogoV67: v(67),
    ringRecipesV67: v(67),
    // v68: getBandLayout is a NEW method name (RPC has no timeout); the pose
    // rides setBandLayout's optional second argument. The look and the
    // junction options ride existing names — aliases under the rows they gate.
    ringPoseV68: v(68),
    ringLookV68: v(68),
    ringJunctionV68: v(68),
    // v51: `setMilgrain` is a NEW METHOD NAME, so this flag is not optional —
    // RPC has no timeout and calling it on a v50 viewer hangs the promise.
    ringMilgrainV51: v(51),
    // v52: `applyFreePattern` is an EXISTING method name gaining an optional
    // trailing argument, which RpcHost's arg spreading makes safe on its own —
    // an older viewer simply ignores it. The flag exists so the host does not
    // show controls that would silently do nothing.
    ringFreePatternV52: v(52),
    // v68 (HDR editor): `pickSurface` is a NEW method name — RPC has no
    // timeout, so HDR Studio's Place highlight stays disabled without it.
    hdrPickV68: v(68),
    // v68 (HDR editor): stones may share the scene env (the leak fix above).
    hdrStonesShareSceneV68: v(68),
    // v68 (HDR editor): `loadEnvFromPixels` is a NEW method name.
    hdrEnvPixelsV68: v(68),
  };
}
